import Handlebars from 'handlebars';
import helpers from '@budibase/handlebars-helpers';
import moment from 'moment-timezone';
import numbro from 'numbro';
import { get, groupBy } from 'lodash';

import store from '../store';
import { getParticularPreferenceSettings } from '../common/selectors/preference';

function formatDateTime(value, format, tz) {
  if (!value) return '';

  const date = moment(value);

  if (!date.isValid) return '';

  if (tz && typeof tz === 'string') {
    const isKrispTz = tz.includes('timezone.gmt');

    if (isKrispTz) {
      const formattedTz = tz.replace('timezone.gmt', '').split('.')[0];
      date.utcOffset(formattedTz);
    } else {
      date.tz(tz);
    }
  }

  const formattedDate = date.format(format);
  return formattedDate;
}

Handlebars.registerHelper('formatDateTime', (value, options) => {
  const { format, tz } = options.hash || {};
  const state = store.getState();
  const timeFormat = (
    getParticularPreferenceSettings(state, 'date_time_format', ['value']) || {}
  ).value;

  const parsedFormat = format || timeFormat || 'l LTS';
  const formattedDateTime = formatDateTime(value, parsedFormat, tz);
  return formattedDateTime;
});

Handlebars.registerHelper('formatDate', (value, options) => {
  const { format, tz } = options.hash || {};
  const state = store.getState();
  const timeFormat = (
    getParticularPreferenceSettings(state, 'date_format', ['value']) || {}
  ).value;

  const parsedFormat = format || timeFormat || 'l';
  const formatDate = formatDateTime(value, parsedFormat, tz);
  return formatDate;
});

Handlebars.registerHelper('formatTime', (value, options) => {
  const { format, tz } = options.hash || {};
  const state = store.getState();
  const timeFormat = (
    getParticularPreferenceSettings(state, 'time_format', ['value']) || {}
  ).value;

  const parsedFormat = format || timeFormat || 'LTS';
  const formatTime = formatDateTime(value, parsedFormat, tz);
  return formatTime;
});

Handlebars.registerHelper('formatNumber', (value, options) => {
  const { mantissa = 0, thousandSeparated = false } = options.hash || {};
  const parsedValue = Number(value) || 0;
  const formattedNumber = numbro(parsedValue).format({ mantissa, thousandSeparated });
  return formattedNumber;
});

Handlebars.registerHelper('getLinesTotalQuantity', (value, options) => {
  if (!Array.isArray(value) || !value.length) return 0;

  const {
    excludes = 'promotion,discount,coupon,giftcertificate,gratuity,payment_surcharge,loyalty',
    fieldKey = 'item_type._id',
    overrideField = 'item_uom.uom.name',
    overrideQuantityField = 'uom_quantity',
    quantityKey = 'quantity',
  } = options.hash || {};

  const excludesStrBuff = excludes.split(',');
  const totalQuantity = value.reduce((total, line) => {
    const currType = get(line, fieldKey);

    if (excludesStrBuff.includes(currType)) return total;
    
    let currQuantity = Number(get(line, quantityKey) || 0);

    if (get(line, overrideField)) {
      currQuantity = Number(get(line, overrideQuantityField) || 0);
    }

    const currTotalQuantity = currQuantity + total;
    return currTotalQuantity;
  }, 0);

  return totalQuantity;
});

Handlebars.registerHelper('currentLink', function() {
  return window.location.href;
});

Handlebars.registerHelper('date', function(options) {
    const { hash } = options || {};
    const { format, toISOString, value, ...otherOpts } = hash || {};

    const date = moment(value);
    const keys = Object.keys(otherOpts);

    for (const key of keys) {
      if (typeof date[key] !== 'function') continue;

      const param = hash[key];

      switch (typeof param) {
        case 'string':
          const params = param.split(',');
          const parsedParams = params.map((param) => Number(param) || param);
          date[key](...parsedParams);
          break;

        default:
          date[key](param);
          break;
      }
    }

    if (toISOString) {
      return date.toISOString();
    }

    return date.format(format);
});

Handlebars.registerHelper('groupBy', (value, options) => {
  const { key } = options.hash || {};

  if (!key
    || !value
    || !Array.isArray(value)
    || !value.length
  ) return [];

  const groupedArray = groupBy(value, key);
  return groupedArray;
});

const handlebarsHelpers = helpers();
Handlebars.registerHelper(handlebarsHelpers);

export default Handlebars;
